import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { SharedModule } from "../../../../shared/shared.module";
import { CommonModule, DecimalPipe } from "@angular/common";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { CaGroupBy, REPARTITION_CA_DATA, RepartitionCaDetail } from "../../models/data.model";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { LotCardComponent } from "../../../../shared/components/lot-card/lot-card.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { exportRepartitionCaExcel } from "./repartition-ca-excel.utils";

export interface RepartitionCaDialogData {
  title: string;
  items: { denomination: string; total: number }[];
  details: RepartitionCaDetail[];
  total: number;
  groupBy: CaGroupBy;
  colorMap: Record<string, string>;
  fullData: REPARTITION_CA_DATA;
  /** État initial du toggle MO (hérité du widget). Par défaut `true`. */
  includeMO?: boolean;
  /** État initial du toggle remise pied (hérité du widget). Par défaut `true`. */
  includeRemisePied?: boolean;
}

interface LotRow {
  lotId: string | null;
  lotNumero: string;
  quantite: number;
  quantiteUG: number;
  prixUnitaire: number;
  coutUnitaire: number;
  uniteVente: string;
  uniteGestion: string;
  ca: number;
  coutMat: number;
  coutMO: number;
  marge: number;
  tauxMarge: number;
}

interface ArticleRow {
  denomination: string;
  ca: number;
  cout: number;
  marge: number;
  tauxMarge: number;
  percentage: string;
  lots: LotRow[];
}

interface SubRow {
  denomination: string;
  ca: number;
  cout: number;
  marge: number;
  tauxMarge: number;
  percentage: string;
  /** Lots directs (mode 3 niveaux : groupe → sub → lots). Vide en mode 4
   *  niveaux où les lots sont regroupés par article (cf. `articles`). */
  lots: LotRow[];
  /** Niveau article intercalé entre `sub` et les lots — non null uniquement
   *  pour `byGroupeTiers` où il manquerait sinon le nom du produit. */
  articles?: ArticleRow[];
}

interface GroupRow {
  denomination: string;
  ca: number;
  cout: number;
  marge: number;
  tauxMarge: number;
  percentage: string;
  subs: SubRow[];
}

interface Stats {
  caFormatted: string;
  coutFormatted: string;
  margeFormatted: string;
  tauxMarge: number;
  count: number;
  countLabel: string;
  pareto80Count: number;
}

@Component({
  selector: "vl-repartition-ca-dialog",
  standalone: true,
  imports: [CommonModule, SharedModule, DialogHeaderExportComponent, DecimalPipe, MatIcon, MatTooltip, RadioCardComponent, LotCardComponent, ChipsToggleComponent, MatSlideToggleModule],
  templateUrl: "./repartition-ca-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepartitionCaDialogComponent {
  data = inject<RepartitionCaDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<RepartitionCaDialogComponent>);

  expanded = signal<Set<string>>(new Set());

  groupBy = signal<CaGroupBy>(this.data.groupBy);
  /** Toggle MO local — initialisé depuis l'état du widget, modifiable dans le dialog. */
  includeMO = signal<boolean>(this.data.includeMO ?? true);
  /** Toggle remise pied local — initialisé depuis l'état du widget. */
  includeRemisePied = signal<boolean>(this.data.includeRemisePied ?? true);
  groupByElements = [
    { name: "Client", value: "byClient" as CaGroupBy },
    { name: "Article", value: "byArticle" as CaGroupBy },
    { name: "Famille", value: "byFamille" as CaGroupBy },
    { name: "Groupe", value: "byGroupeTiers" as CaGroupBy },
  ];

  private groupByLabels: Record<CaGroupBy, string> = {
    byClient: "Répartition CA — Client",
    byArticle: "Répartition CA — Article",
    byFamille: "Répartition CA — Famille",
    byGroupeTiers: "Répartition CA — Groupe de tiers",
  };

  title = computed(() => this.groupByLabels[this.groupBy()]);

  private activeItems = computed(() => {
    const fd = this.data.fullData;
    if (!fd) return [];
    return (fd[this.groupBy()] ?? []).filter((d: any) => d.total > 0).sort((a: any, b: any) => b.total - a.total);
  });

  private groupLabels: Record<CaGroupBy, string> = {
    byClient: "clients", byArticle: "articles", byFamille: "familles", byGroupeTiers: "groupes",
  };

  private groupField: Record<CaGroupBy, keyof RepartitionCaDetail> = {
    byClient: "tiersDenomination", byArticle: "articleDenomination",
    byFamille: "familleDenomination", byGroupeTiers: "tiersGroupe",
  };

  private subField: Record<CaGroupBy, keyof RepartitionCaDetail> = {
    byClient: "articleDenomination", byArticle: "tiersDenomination",
    byFamille: "articleDenomination", byGroupeTiers: "tiersDenomination",
  };

  /**
   * Niveau intermédiaire (entre sub et lots) — défini uniquement quand sub
   * n'est pas déjà l'article ET que le groupBy de plus haut niveau n'est pas
   * non plus l'article. Sinon le tableau de lots reste à plat.
   *
   * Cas byGroupeTiers : groupe → tiers → **article** → lots. Sans cette ligne
   * on lirait juste des numéros de lot (ex. K 113 A) sans savoir de quel
   * produit ils viennent.
   */
  private articleField: Record<CaGroupBy, keyof RepartitionCaDetail | null> = {
    byClient: null,
    byArticle: null,
    byFamille: null,
    byGroupeTiers: "articleDenomination",
  };

  /** Coût d'une ligne, conditionné par le toggle MO local. */
  private coutOf(d: RepartitionCaDetail): number {
    return (d.coutMateriel ?? 0) + (this.includeMO() ? (d.coutMO ?? 0) : 0);
  }

  /** Montant HT d'une ligne selon le toggle `includeRemisePied`. */
  private montantOf(d: RepartitionCaDetail): number {
    return this.includeRemisePied() ? d.montantHT : (d.montantBrut ?? d.montantHT);
  }

  stats = computed<Stats>(() => {
    const details = this.data.details ?? [];
    const totalCa = details.reduce((s, d) => s + this.montantOf(d), 0);
    const totalCout = details.reduce((s, d) => s + this.coutOf(d), 0);
    const marge = totalCa - totalCout;

    const items = this.activeItems();
    let cumul = 0, pareto80Count = 0;
    for (const item of items) { cumul += item.total; pareto80Count++; if (cumul >= totalCa * 0.8) break; }

    return {
      caFormatted: WidgetHelperService.formatNumeric(totalCa) + " €",
      coutFormatted: WidgetHelperService.formatNumeric(totalCout) + " €",
      margeFormatted: WidgetHelperService.formatNumeric(marge) + " €",
      tauxMarge: totalCa > 0 ? (marge / totalCa) * 100 : 0,
      count: items.length,
      countLabel: this.groupLabels[this.groupBy()],
      pareto80Count,
    };
  });

  margeColor = computed(() => {
    const t = this.stats().tauxMarge;
    if (t >= 35) return "valid";
    if (t >= 20) return "warn";
    return "error";
  });

  rows = computed<GroupRow[]>(() => {
    const totalCa = this.data.total || 1;
    const sorted = this.activeItems();
    const gField = this.groupField[this.groupBy()];
    const sField = this.subField[this.groupBy()];
    const aField = this.articleField[this.groupBy()];
    const details = this.data.details ?? [];
    const withMo = this.includeMO();

    type LotAccum = { lotId: string | null; ca: number; mat: number; mo: number; qty: number; qtyUG: number; prix: number; coutU: number; uv: string; ug: string };
    type ArticleAccum = { ca: number; cout: number; lots: Map<string, LotAccum> };
    type SubAccum = { ca: number; cout: number; lots: Map<string, LotAccum>; articles: Map<string, ArticleAccum> };

    const buildLotRow = (lotNumero: string, l: LotAccum): LotRow => {
      const coutLot = l.mat + (withMo ? l.mo : 0);
      return {
        lotId: l.lotId ?? null,
        lotNumero,
        quantite: l.qty,
        quantiteUG: l.qtyUG,
        prixUnitaire: l.prix,
        uniteVente: l.uv,
        uniteGestion: l.ug,
        coutUnitaire: l.coutU,
        ca: l.ca,
        coutMat: l.mat,
        coutMO: l.mo,
        marge: l.ca - coutLot,
        tauxMarge: l.ca > 0 ? ((l.ca - coutLot) / l.ca) * 100 : 0,
      };
    };

    const accumulateLot = (lotsMap: Map<string, LotAccum>, d: RepartitionCaDetail, montant: number) => {
      if (!d.lotNumero) return;
      if (!lotsMap.has(d.lotNumero)) {
        lotsMap.set(d.lotNumero, { lotId: d.lotId ?? null, ca: 0, mat: 0, mo: 0, qty: 0, qtyUG: 0, prix: d.prixUnitaire ?? 0, coutU: d.coutUnitaire ?? 0, uv: d.uniteVente ?? '', ug: d.uniteGestion ?? '' });
      }
      const lot = lotsMap.get(d.lotNumero)!;
      if (!lot.lotId && d.lotId) lot.lotId = d.lotId;
      lot.ca += montant;
      lot.mat += d.coutMateriel ?? 0;
      lot.mo += d.coutMO ?? 0;
      lot.qty += d.quantite ?? 0;
      lot.qtyUG += d.quantiteUG ?? 0;
    };

    return sorted.map((item) => {
      const subMap = new Map<string, SubAccum>();

      for (const d of details) {
        if (String(d[gField]) !== item.denomination) continue;
        const subKey = String(d[sField]);
        if (!subMap.has(subKey)) {
          subMap.set(subKey, { ca: 0, cout: 0, lots: new Map(), articles: new Map() });
        }
        const entry = subMap.get(subKey)!;
        const cout = this.coutOf(d);
        const montant = this.montantOf(d);
        entry.ca += montant;
        entry.cout += cout;

        if (aField) {
          // 4 niveaux : on groupe les lots par article au sein du sub.
          const aKey = String(d[aField]);
          if (!entry.articles.has(aKey)) {
            entry.articles.set(aKey, { ca: 0, cout: 0, lots: new Map() });
          }
          const art = entry.articles.get(aKey)!;
          art.ca += montant;
          art.cout += cout;
          accumulateLot(art.lots, d, montant);
        } else {
          // 3 niveaux : lots à plat sous le sub.
          accumulateLot(entry.lots, d, montant);
        }
      }

      const subs: SubRow[] = [...subMap.entries()]
        .map(([denomination, v]) => {
          const subPercent = ((v.ca / totalCa) * 100).toFixed(1);
          if (aField) {
            const articles: ArticleRow[] = [...v.articles.entries()]
              .map(([artName, a]) => {
                const lots = [...a.lots.entries()]
                  .map(([n, l]) => buildLotRow(n, l))
                  .sort((x, y) => y.ca - x.ca);
                return {
                  denomination: artName,
                  ca: a.ca,
                  cout: a.cout,
                  marge: a.ca - a.cout,
                  tauxMarge: a.ca > 0 ? ((a.ca - a.cout) / a.ca) * 100 : 0,
                  percentage: a.ca > 0 ? ((a.ca / v.ca) * 100).toFixed(1) : '0.0',
                  lots,
                };
              })
              .sort((x, y) => y.ca - x.ca);
            return {
              denomination,
              ca: v.ca,
              cout: v.cout,
              marge: v.ca - v.cout,
              tauxMarge: v.ca > 0 ? ((v.ca - v.cout) / v.ca) * 100 : 0,
              percentage: subPercent,
              lots: [],
              articles,
            };
          }
          const lots = [...v.lots.entries()]
            .map(([n, l]) => buildLotRow(n, l))
            .sort((x, y) => y.ca - x.ca);
          return {
            denomination,
            ca: v.ca,
            cout: v.cout,
            marge: v.ca - v.cout,
            tauxMarge: v.ca > 0 ? ((v.ca - v.cout) / v.ca) * 100 : 0,
            percentage: subPercent,
            lots,
          };
        })
        .sort((a, b) => b.ca - a.ca);

      const groupCout = subs.reduce((s, sub) => s + sub.cout, 0);
      const groupMarge = item.total - groupCout;

      return {
        denomination: item.denomination,
        ca: item.total,
        cout: groupCout,
        marge: groupMarge,
        tauxMarge: item.total > 0 ? (groupMarge / item.total) * 100 : 0,
        percentage: ((item.total / totalCa) * 100).toFixed(1),
        subs,
      };
    });
  });

  toggle(key: string) {
    const current = new Set(this.expanded());
    current.has(key) ? current.delete(key) : current.add(key);
    this.expanded.set(current);
  }

  isOpen(key: string): boolean {
    return this.expanded().has(key);
  }

  getColor(denomination: string): string | null {
    return this.data.colorMap?.[denomination] ?? null;
  }

  margeClass(taux: number): string {
    if (taux >= 35) return "on-valid";
    if (taux >= 20) return "on-warning";
    return "on-error";
  }

  exporting = signal(false);

  async exportExcel() {
    this.exporting.set(true);
    try {
      await exportRepartitionCaExcel(this.data.fullData, {
        includeMO: this.includeMO(),
        includeRemisePied: this.includeRemisePied(),
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
